import { Box } from '@material-ui/core'
import { useSnackbar } from 'notistack'
import { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Button, FormattedText, FormControl, Loader, ToggleSwitch, Typography } from 'src/modules/core/components'
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from 'src/modules/notification-preferences/hooks/use-notification-preferences'
import type { OtherSettingsNotificationPreferencesFormHandle } from 'src/modules/user/components/settings-profile-info/other-settings-form-handles'
import classes from './settings-email-notifications-card.module.scss'

type NotificationPreferencesFormData = {
  email_new_booking: boolean
  email_booking_changed: boolean
  email_appointment_confirmed: boolean
  email_appointment_cancelled: boolean
  email_inbound_sms_reply: boolean
}

export type SettingsEmailNotificationsCardProps = {
  hideSubmitButton?: boolean
  onDirtyChange?: (dirty: boolean) => void
}

export const SettingsEmailNotificationsCard = forwardRef<
  OtherSettingsNotificationPreferencesFormHandle,
  SettingsEmailNotificationsCardProps
>(function SettingsEmailNotificationsCard({ hideSubmitButton = false, onDirtyChange }, ref) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const preferencesQuery = useNotificationPreferences()
  const updatePreferences = useUpdateNotificationPreferences()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { isDirty },
  } = useForm<NotificationPreferencesFormData>({
    defaultValues: {
      email_new_booking: true,
      email_booking_changed: true,
      email_appointment_confirmed: true,
      email_appointment_cancelled: true,
      email_inbound_sms_reply: true,
    },
  })

  useEffect(() => {
    const data = preferencesQuery.data
    if (!data) {
      return
    }
    reset({
      email_new_booking: data.email_new_booking,
      email_booking_changed: data.email_booking_changed,
      email_appointment_confirmed: data.email_appointment_confirmed,
      email_appointment_cancelled: data.email_appointment_cancelled,
      email_inbound_sms_reply: data.email_inbound_sms_reply,
    })
  }, [preferencesQuery.data, reset])

  const persistIfDirty = useCallback(async (): Promise<boolean> => {
    if (!isDirty) {
      return false
    }
    const data = getValues()
    await updatePreferences.mutateAsync(data)
    reset(data)
    return true
  }, [getValues, isDirty, reset, updatePreferences])

  useImperativeHandle(
    ref,
    () => ({
      submitIfDirty: persistIfDirty,
    }),
    [persistIfDirty],
  )

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const onSubmit = (data: NotificationPreferencesFormData) => {
    updatePreferences
      .mutateAsync(data)
      .then(() => {
        reset(data)
        if (!hideSubmitButton) {
          enqueueSnackbar(formatMessage({ id: 'settings.saved' }), { variant: 'success' })
        }
      })
      .catch(() => {
        /* errors surface via global handlers / invalidation */
      })
  }

  if (!preferencesQuery.isLoading && (preferencesQuery.isError || !preferencesQuery.data)) {
    return null
  }

  if (preferencesQuery.isLoading) {
    return <Loader isLoading={true} />
  }

  const emailNewBooking = watch('email_new_booking')
  const emailBookingChanged = watch('email_booking_changed')
  const emailAppointmentConfirmed = watch('email_appointment_confirmed')
  const emailAppointmentCancelled = watch('email_appointment_cancelled')
  const emailInboundSmsReply = watch('email_inbound_sms_reply')

  return (
    <Box
      className={classes.card}
      component="section"
      aria-label={formatMessage({ id: 'settings.notificationPreferences.title' })}
    >
      <Typography variant="h5" className={classes.title}>
        <FormattedText id="settings.notificationPreferences.title" />
      </Typography>
      <Typography variant="body" className={classes.description}>
        <FormattedText id="settings.notificationPreferences.description" />
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl
          label={formatMessage({ id: 'settings.notificationPreferences.newBooking' })}
          labelClassName={classes.label}
        >
          <Box className={classes.row}>
            <Controller
              control={control}
              name="email_new_booking"
              render={({ field: { onChange, value, ref: fieldRef } }) => (
                <ToggleSwitch
                  ref={fieldRef}
                  checked={value}
                  labelClassName={classes.switchLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
            <Typography variant="body" className={classes.infoText}>
              <FormattedText id={emailNewBooking ? 'common.enabled' : 'common.disabled'} />
            </Typography>
          </Box>
        </FormControl>

        <FormControl
          label={formatMessage({ id: 'settings.notificationPreferences.bookingChanged' })}
          labelClassName={classes.label}
        >
          <Box className={classes.row}>
            <Controller
              control={control}
              name="email_booking_changed"
              render={({ field: { onChange, value, ref: fieldRef } }) => (
                <ToggleSwitch
                  ref={fieldRef}
                  checked={value}
                  labelClassName={classes.switchLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
            <Typography variant="body" className={classes.infoText}>
              <FormattedText id={emailBookingChanged ? 'common.enabled' : 'common.disabled'} />
            </Typography>
          </Box>
        </FormControl>

        <FormControl
          label={formatMessage({ id: 'settings.notificationPreferences.appointmentConfirmed' })}
          labelClassName={classes.label}
        >
          <Box className={classes.row}>
            <Controller
              control={control}
              name="email_appointment_confirmed"
              render={({ field: { onChange, value, ref: fieldRef } }) => (
                <ToggleSwitch
                  ref={fieldRef}
                  checked={value}
                  labelClassName={classes.switchLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
            <Typography variant="body" className={classes.infoText}>
              <FormattedText id={emailAppointmentConfirmed ? 'common.enabled' : 'common.disabled'} />
            </Typography>
          </Box>
        </FormControl>

        <FormControl
          label={formatMessage({ id: 'settings.notificationPreferences.appointmentCancelled' })}
          labelClassName={classes.label}
        >
          <Box className={classes.row}>
            <Controller
              control={control}
              name="email_appointment_cancelled"
              render={({ field: { onChange, value, ref: fieldRef } }) => (
                <ToggleSwitch
                  ref={fieldRef}
                  checked={value}
                  labelClassName={classes.switchLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
            <Typography variant="body" className={classes.infoText}>
              <FormattedText id={emailAppointmentCancelled ? 'common.enabled' : 'common.disabled'} />
            </Typography>
          </Box>
        </FormControl>

        <FormControl
          label={formatMessage({ id: 'settings.notificationPreferences.smsReplyFromClient' })}
          labelClassName={classes.label}
        >
          <Box className={classes.row}>
            <Controller
              control={control}
              name="email_inbound_sms_reply"
              render={({ field: { onChange, value, ref: fieldRef } }) => (
                <ToggleSwitch
                  ref={fieldRef}
                  checked={value}
                  labelClassName={classes.switchLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
            <Typography variant="body" className={classes.infoText}>
              <FormattedText id={emailInboundSmsReply ? 'common.enabled' : 'common.disabled'} />
            </Typography>
          </Box>
        </FormControl>

        {!hideSubmitButton && (
          <Button
            type="submit"
            className={classes.button}
            widthType="content"
            isLoading={updatePreferences.isLoading}
            disabled={!isDirty}
          >
            <FormattedText id="common.save" />
          </Button>
        )}
      </form>
    </Box>
  )
})
